<template>
  <div class="grid-container mt-6 md:mt-14">
    <Head :title="formAction == 'store' ? 'Nueva oferta de trabajo' : 'Editar oferta de trabajo'" />
    <div class="flex justify-between items-center">
      <header>
        <h1 class="headline-md text-primary font-bold md:display-sm">
          {{ formAction == 'store' ? 'Nueva' : 'Editar' }} oferta de trabajo
        </h1>
        <p v-if="formAction == 'store'" class="title-lg text-secondary">
          Completa los campos requeridos para crear una nueva oferta de trabajo
        </p>
      </header>
      <div class="hidden md:inline-block">
        <Button
          type="submit"
          form="job-posting-form"
          :icon="formAction == 'store' ? 'add' : 'round-save'"
          size="large"
          class="btn-filled h-14"
          :isLoading="form.processing"
          :disabled="formAction == 'update' && !form.isDirty"
        >
          {{ formAction == 'store' ? 'Crear nueva oferta' : 'Guardar cambios' }}
        </Button>
      </div>
    </div>
    <form
      id="job-posting-form"
      class="my-4 md:grid md:grid-cols-3 md:gap-6"
      @submit.prevent="submitForm"
    >
      <div>
        <div class="md:p-6 md:border md:border-outline/16 md:rounded-2xl md:surface1">
          <h2 class="title-lg text-primary30 font-medium my-3">Información básica</h2>
          <TextInput
            id="title"
            v-model="form.title"
            label="Título"
            required
            :error="errors.title"
          />
          <TextInput
            id="offered_position"
            v-model="form.offered_position"
            label="Cargo ofertado"
            required
            :error="errors.offered_position"
          />
          <TextInput
            id="vacancies"
            v-model="form.vacancies"
            label="Vacantes"
            helper
            type="number"
            min="1"
            required
            hint="Cantidad de vacantes disponibles para la plaza"
            :error="errors.vacancies"
          />
          <FormSelect
            id="job_category_id"
            v-model="form.job_category_id"
            label="Categoría"
            required
          >
            <option value="" disabled>Seleccionar</option>
            <option v-for="category in jobCategoryOptions" :key="category.id" :value="category.id">
              {{ category.name }}
            </option>
          </FormSelect>
          <FormSelect
            id="job_contract_type_id"
            v-model="form.job_contract_type_id"
            label="Tipo de contrato"
            required
          >
            <option value="" disabled>Seleccionar</option>
            <option v-for="contract in contractTypeOptions" :key="contract.id" :value="contract.id">
              {{ contract.name }}
            </option>
          </FormSelect>
          <FormSelect
            id="education_level_id"
            v-model="form.education_level_id"
            label="Escolaridad requerida"
            required
          >
            <option value="" disabled>Seleccionar</option>
            <option v-for="level in educationLevelOptions" :key="level.id" :value="level.id">
              {{ level.name }}
            </option>
          </FormSelect>
          <h3 class="title-md text-primary20 font-medium my-3">Ubicación</h3>
          <div>
            <CheckInput
              id="is_remote"
              v-model="form.is_remote"
              label="La plaza es de trabajo remoto"
              check-left
              class="mb-2"
              :errors="errors.is_remote"
            />
            <transition name="slide-fade">
              <FormSelect
                v-if="!form.is_remote"
                id="department_id"
                v-model="form.department_id"
                label="Departamento"
                required
              >
                <option value="" disabled>Seleccionar</option>
                <option v-for="item in departmentsOptions" :key="item.id" :value="item.id">
                  {{ item.name }}
                </option>
              </FormSelect>
            </transition>

            <transition name="slide-fade">
              <FormSelect
                v-if="!form.is_remote && municipalities.length > 0"
                id="municipality_id"
                v-model="form.municipality_id"
                label="Municipio"
                required
              >
                <option value="" disabled>Seleccionar</option>
                <option
                  v-for="item in municipalities"
                  :key="item.municipality.id"
                  :value="item.municipality.id"
                >
                  {{ item.municipality.name }}
                </option>
              </FormSelect>
            </transition>
          </div>
        </div>
      </div>
      <div>
        <div class="md:p-6 md:border md:border-outline/16 md:rounded-2xl md:surface1">
          <h2 class="title-lg text-primary30 font-medium my-3">Descripción de la oferta</h2>
          <CheckInput
            id="provides_law_benefits"
            v-model="form.provides_law_benefits"
            label="¿Esta plaza ofrece prestaciones de ley?"
            check-left
            :error="errors.provides_law_benefits"
            class="py-4"
          />
          <FormTextarea
            id="short_description"
            v-model="form.short_description"
            label="Descripción corta"
            hint="Esta descripción se usará en las tarjetas de resultados"
            required
            :maxlength="300"
          />
          <FormTextarea
            id="full_description"
            v-model="form.full_description"
            label="Descripción completa"
            hint="Esta descripción se usará en el perfil de la oferta de trabajo"
            required
            :maxlength="2000"
            :rows="5"
          />
          <FormTextarea
            id="required_qualifications"
            v-model="form.required_qualifications"
            label="Requisitos"
            :maxlength="500"
            :rows="5"
          />
          <FormTextarea
            id="job_responsibilities"
            v-model="form.job_responsibilities"
            label="Funciones y responsabilidades"
            :maxlength="500"
            :rows="5"
          />
          <FormTextarea
            id="benefits_and_perks"
            v-model="form.benefits_and_perks"
            label="Prestaciones adicionales"
            :maxlength="500"
            :rows="5"
          />
        </div>
      </div>
      <div>
        <div class="md:p-6 md:border md:border-outline/16 md:rounded-2xl md:surface1">
          <h2 class="title-lg text-primary30 font-medium my-3">Salario</h2>
          <CheckInput
            id="salary_to_be_agreed"
            v-model="form.salary_to_be_agreed"
            label="¿Salario es a convenir con el postulante?"
            check-left
            :error="errors.salary_to_be_agreed"
            class="py-4"
          />
          <transition name="slide-fade">
            <div v-if="!form.salary_to_be_agreed">
              <p class="title-md text-primary20 font-medium my-3">Ingresa el rango salarial</p>
              <TextInput
                id="salary_min"
                v-model="form.salary_min"
                label="Salarío mínimo"
                type="number"
                min="0"
                required
              />
              <TextInput
                id="salary_max"
                v-model="form.salary_max"
                label="Salarío máximo"
                type="number"
                min="0"
                required
              />
            </div>
          </transition>
          <FormSelect id="pay_type_id" v-model="form.pay_type_id" label="Tipo de paga" required>
            <option value="" disabled>Seleccionar</option>
            <option v-for="item in payTypeOptions" :key="item.id" :value="item.id">
              {{ item.name }}
            </option>
          </FormSelect>
        </div>
      </div>
    </form>
    <Button
      type="submit"
      form="job-posting-form"
      :icon="formAction == 'store' ? 'add' : 'round-save'"
      size="large"
      class="btn-filled h-14 mx-auto mt-10"
      :isLoading="form.processing"
      :disabled="formAction == 'update' && !form.isDirty"
    >
      {{ this.formAction == 'store' ? 'Crear nueva oferta' : 'Guardar cambios' }}
    </Button>
  </div>
</template>

<script>
import { cleanNullValuesFromObject } from '@/utilities.helpers.js'
import evalUserProfileType from '@/evalUserProfileType.mixin.js'
import { Head } from '@inertiajs/inertia-vue'
import Layout from '@/Layouts/Layout'
import Button from '@/Components/Button'
import TextInput from '@/Components/FormInputText'
import FormTextarea from '@/Components/FormTextarea'
import FormSelect from '@/Components/FormSelect'
import CheckInput from '@/Components/FormInputCheck'

export default {
  name: 'companyJobsForm',
  layout: Layout,
  components: { Head, Button, TextInput, FormTextarea, FormSelect, CheckInput },
  props: {
    formAction: {
      type: String,
      required: true,
      validator(value) {
        return ['store', 'update'].includes(value)
      },
    },
    previousData: {
      type: Object,
      required: false,
      default: () => {
        return {
          id: null,
          title: '',
          offered_position: '',
          vacancies: 1,
          job_category_id: '',
          job_contract_type_id: '',
          education_level_id: '',
          pay_type_id: '',
          short_description: '',
          full_description: '',
          required_qualifications: '',
          job_responsibilities: '',
          benefits_and_perks: '',
          provides_law_benefits: true,
          salary_to_be_agreed: false,
          salary_min: 0,
          salary_max: 0,
          is_remote: false,
          department_id: null,
          municipality_id: null,
        }
      },
    },
    errors: {
      type: Object,
      required: false,
    },
    jobCategoryOptions: { type: Array, required: true },
    educationLevelOptions: { type: Array, required: true },
    contractTypeOptions: { type: Array, required: true },
    departmentsOptions: { type: Array, required: true },
    municipalitiesOptions: { type: Array, required: true },
    payTypeOptions: { type: Array, required: true },
  },
  data() {
    return {
      form: this.$inertia.form(this.previousData),
    }
  },
  computed: {
    municipalities() {
      return this.municipalitiesOptions.filter(
        item => item.department.id == this.form.department_id,
      )
    },
  },
  methods: {
    submitForm() {
      const requestOptions = {
        preserveState: true,
        onError: errors => {
          this.$swal.fire({
            title: '¡Ocurrió un error!',
            // TODO: Agregar renderizacion de mensajes de error
            text: `Por favor, revisa los datos e intenta de nuevo.`,
          })
        },
      }
      const submitForm = this.form.transform(data => {
        return { ...cleanNullValuesFromObject(data), location_id: data.municipality_id }
      })

      if (this.formAction == 'store')
        submitForm.post(this.$route('company.jobs.store'), requestOptions)
      else if (this.formAction == 'update')
        submitForm.put(this.$route('company.jobs.update', this.form.id), requestOptions)
    },
  },
}
</script>
